import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XMarkIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface Tier {
  id: string;
  name: string;
  monthlyPrice: number;
  monthlyWords: number;
  welcomeWords: number;
  features: string[];
  highlight: boolean;
}

interface Status {
  plan: string;
  status: string;
  balance: number;
  monthlyWords: number;
  monthlyWordsUsed: number;
  nextRenewal: string | null;
  canceledAt: string | null;
  /** A live subscription backs the row; false for a legacy plan with nothing to manage. */
  managed?: boolean;
  /** A complimentary plan: nothing to buy, change or cancel here. */
  comped?: boolean;
  /** Who bills the plan; 'app_store' = bought in the iPhone app, managed there. */
  billing?: 'app_store' | 'stripe' | 'comped' | 'none';
}

const fmt = (n: number) => n.toLocaleString();

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [status, setStatus] = useState<Status | null>(null);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingChange, setPendingChange] = useState<Tier | null>(null);

  const load = useCallback(async () => {
    try {
      const [tiersRes, statusRes] = await Promise.all([
        axios.get('/subscriptions/tiers'),
        axios.get('/subscriptions/status'),
      ]);
      setTiers(tiersRes.data.plans || []);
      setStatus(statusRes.data);
    } catch {
      toast.error(t('subscription.loadError', 'Could not load plans'));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (params.get('checkout') === 'success') {
      toast.success(t('subscription.checkoutSuccess', 'Welcome aboard — your AI words are ready.'));
    }
  }, [params, t]);

  const busy = isLoading !== null;
  // A plan name left on a dead subscription (legacy rows) is not "current".
  const liveStatus = ['active', 'trialing', 'past_due', 'unpaid', 'incomplete', 'paused'].includes(
    status?.status || '',
  );
  const currentPlan = status?.plan && status.plan !== 'free' && liveStatus ? status.plan : 'free';
  const order = tiers.map((x) => x.id);
  const idx = (id: string) => order.indexOf(id);
  // Manage/cancel only exist for a subscription Stripe knows; a legacy plan label with no
  // subscription behind it is shown as current but a new plan goes through Checkout.
  const hasActiveSubscription = currentPlan !== 'free' && status?.managed !== false;
  const pendingCancel = hasActiveSubscription && !!status?.canceledAt;
  // Bought in the iPhone app: Apple bills it, so it is changed or cancelled there.
  const appStore = status?.billing === 'app_store';

  const featureLabel = (f: string) =>
    ({
      ai_writing: t('pricing.features.aiWriting', 'AI co-writing in every story'),
      export_text: t('pricing.features.exportText', 'Text export'),
      export_fountain: t('pricing.features.exportFountain', 'Screenplay (Fountain) export'),
      advanced_model: t('pricing.features.advancedModel', 'Advanced AI models'),
      premium_models: t('pricing.features.premiumModels', 'Premium models (GPT-5.6 Sol, Grok 4.6)'),
      priority_support: t('pricing.features.prioritySupport', 'Priority support'),
    })[f] || f;

  const choose = async (tier: Tier, confirmed = false) => {
    if (tier.id === currentPlan) return;
    if (tier.id === 'free') {
      setShowCancelModal(true);
      return;
    }
    // A plan change on a live subscription moves money now: ask first.
    if (hasActiveSubscription && !confirmed) {
      setPendingChange(tier);
      return;
    }
    setPendingChange(null);
    setIsLoading(tier.id);
    try {
      const successUrl = `${window.location.origin}/subscription?checkout=success`;
      const cancelUrl = `${window.location.origin}/subscription`;
      const res = hasActiveSubscription
        ? await axios.put('/subscriptions/plan', { planId: tier.id, successUrl, cancelUrl })
        : await axios.post('/subscriptions/checkout-session', {
            planId: tier.id,
            successUrl,
            cancelUrl,
          });
      if (res.data.sessionUrl) {
        window.location.href = res.data.sessionUrl;
        return;
      }
      if (res.data.wordsAdded > 0) {
        toast.success(
          t('subscription.upgraded', '{{words}} AI words added to your balance', {
            words: fmt(res.data.wordsAdded),
          }),
        );
      } else {
        toast.success(t('subscription.planChanged', 'Plan updated'));
      }
      await load();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          t('subscription.subscriptionError', 'Could not change your plan. Please try again.'),
      );
    } finally {
      setIsLoading(null);
    }
  };

  const manageBilling = async () => {
    setIsLoading('manage');
    try {
      const res = await axios.post('/subscriptions/portal', { returnUrl: window.location.href });
      if (res.data.url) window.location.href = res.data.url;
    } catch {
      toast.error(t('subscription.portalError', 'Could not open billing. Please try again.'));
    } finally {
      setIsLoading(null);
    }
  };

  const cancel = async () => {
    setIsLoading('cancel');
    try {
      await axios.delete('/subscriptions/cancel');
      setShowCancelModal(false);
      toast.success(
        t('subscription.cancelScheduled', 'Your plan stays active until the end of the period.'),
      );
      await load();
    } catch {
      toast.error(t('subscription.cancelError', 'Could not cancel. Please try again.'));
    } finally {
      setIsLoading(null);
    }
  };

  const resume = async () => {
    setIsLoading('resume');
    try {
      await axios.post('/subscriptions/resume');
      toast.success(t('subscription.resumed', 'Cancellation undone'));
      await load();
    } catch {
      toast.error(t('subscription.resumeError', 'Could not resume. Please try again.'));
    } finally {
      setIsLoading(null);
    }
  };

  const buttonLabel = (tier: Tier) => {
    if (idx(tier.id) > idx(currentPlan)) return t('subscription.upgrade', 'Upgrade');
    if (idx(tier.id) < idx(currentPlan)) return t('subscription.downgrade', 'Downgrade');
    return t('pricing.subscribe', 'Subscribe');
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-light-border dark:border-dark-border">
          <button
            onClick={() => navigate(-1)}
            aria-label={t('common.back', 'Back')}
            className="p-2 hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
          </button>
          <h1 className="text-lg font-semibold text-light-text dark:text-dark-text">
            {t('pricing.title', 'Choose Your Plan')}
          </h1>
          <div className="w-9" />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-3xl mb-3">
              <SparklesIcon className="w-8 h-8 text-primary-500" />
            </div>
            <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-1">
              {t('pricing.headline', 'Words for every draft')}
            </h2>
            <p className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
              {t(
                'pricing.subtitle',
                'Plans refill your AI words every month. Top-ups never expire.',
              )}
            </p>
          </div>

          {status && (
            <div className="rounded-2xl p-4 mb-6 border border-light-border dark:border-dark-border bg-light-surface/80 dark:bg-dark-surface/80">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  {t('subscription.balanceLabel', 'AI words left')}
                </span>
                <span className="text-2xl font-bold text-light-text dark:text-dark-text">
                  {fmt(status.balance)}
                </span>
              </div>
              {status.monthlyWords > 0 &&
                status.nextRenewal &&
                !(appStore && status.canceledAt) && (
                  <p className="text-xs mt-1 text-light-text-secondary dark:text-dark-text-secondary">
                    {t('subscription.refills', '{{words}} more on {{date}}', {
                      words: fmt(status.monthlyWords),
                      date: new Date(status.nextRenewal).toLocaleDateString(),
                    })}
                  </p>
                )}
              {status.comped && (
                <p className="text-xs mt-1 text-primary-500">
                  {t(
                    'subscription.comped',
                    'Your plan is complimentary: no card needed. Top-ups still work.',
                  )}
                </p>
              )}
              {appStore && (
                <p className="text-xs mt-1 text-primary-500">
                  {t(
                    'subscription.appStore',
                    'Your plan is billed through the App Store. Change or cancel it on your iPhone: Settings → your name → Subscriptions.',
                  )}
                </p>
              )}
              {appStore && status.canceledAt && status.nextRenewal && (
                <p className="text-xs mt-1 text-orange-500">
                  {t('subscription.endsOn', 'Plan ends on {{date}}', {
                    date: new Date(status.nextRenewal).toLocaleDateString(),
                  })}
                </p>
              )}
              {pendingCancel && (
                <p className="text-xs mt-1 text-orange-500">
                  {t('subscription.endsOn', 'Plan ends on {{date}}', {
                    date: status.nextRenewal
                      ? new Date(status.nextRenewal).toLocaleDateString()
                      : '—',
                  })}{' '}
                  <button type="button" onClick={resume} disabled={busy} className="underline">
                    {t('subscription.resume', 'Keep my plan')}
                  </button>
                </p>
              )}
            </div>
          )}

          {hasActiveSubscription && (
            <div className="flex gap-2 mb-6">
              <button
                onClick={manageBilling}
                disabled={busy}
                className="flex-1 py-3 bg-light-surface dark:bg-dark-surface hover:bg-gray-100 dark:hover:bg-dark-border border border-light-border dark:border-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
              >
                {isLoading === 'manage'
                  ? t('common.loading', 'Loading...')
                  : t('subscription.manageBilling', 'Manage billing')}
              </button>
              {!pendingCancel && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-semibold rounded-xl transition-colors text-sm"
                >
                  {t('subscription.cancel', 'Cancel')}
                </button>
              )}
            </div>
          )}

          <div className="space-y-4 mb-6">
            {tiers.map((tier) => {
              const isCurrent = currentPlan === tier.id;
              return (
                <div
                  key={tier.id}
                  data-testid={`plan-${tier.id}`}
                  className={`rounded-2xl p-5 border-2 transition-all backdrop-blur-md ${
                    isCurrent
                      ? 'border-green-500 bg-green-500/20'
                      : tier.highlight
                        ? 'border-primary-500 bg-primary-500/20'
                        : 'border-light-border dark:border-dark-border bg-light-surface/80 dark:bg-dark-surface/80'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-light-text dark:text-dark-text">
                        {tier.name}
                      </h3>
                      {tier.highlight && !isCurrent && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full">
                          {t('pricing.popular', 'Popular')}
                        </span>
                      )}
                      {isCurrent && (
                        <span className="bg-green-500/20 text-green-800 dark:text-green-300 text-xs px-2 py-0.5 rounded-full">
                          {t('pricing.currentPlan', 'Current')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold text-light-text dark:text-dark-text">
                      ${tier.monthlyPrice.toFixed(2)}
                    </span>
                    {tier.monthlyPrice > 0 && (
                      <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
                        /{t('pricing.month', 'mo')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-primary-500 mb-3">
                    {tier.monthlyWords > 0
                      ? t('pricing.wordsPerMonth', '{{words}} AI words / month', {
                          words: fmt(tier.monthlyWords),
                        })
                      : t('pricing.welcomeWords', '{{words}} AI words to start — no card needed', {
                          words: fmt(tier.welcomeWords),
                        })}
                  </p>

                  <div className="space-y-2 mb-4">
                    {tier.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <CheckIcon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                        <span className="text-sm text-light-text dark:text-dark-text">
                          {featureLabel(f)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {isCurrent ? (
                    <div className="w-full py-3 text-center text-green-800 dark:text-green-300 font-semibold text-sm">
                      {t('pricing.currentPlan', 'Current')}
                    </div>
                  ) : tier.id === 'free' ? (
                    hasActiveSubscription && !pendingCancel ? (
                      <button
                        onClick={() => setShowCancelModal(true)}
                        className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 font-semibold rounded-xl transition-colors text-sm"
                      >
                        {t('subscription.downgradeToFree', 'Downgrade to Free')}
                      </button>
                    ) : (
                      <div className="w-full py-3 text-center text-light-text-secondary dark:text-dark-text-secondary text-sm">
                        {t('pricing.freePlan', 'Free to start')}
                      </div>
                    )
                  ) : status?.comped || appStore ? null : (
                    <button
                      onClick={() => choose(tier)}
                      disabled={busy}
                      className={`w-full py-3 font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 ${
                        idx(tier.id) < idx(currentPlan)
                          ? 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-500'
                          : 'bg-primary-500 hover:bg-primary-600 text-white'
                      }`}
                    >
                      <SparklesIcon className="w-4 h-4" />
                      {isLoading === tier.id
                        ? t('subscription.processing', 'Processing...')
                        : buttonLabel(tier)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-center text-light-text-secondary dark:text-dark-text-secondary text-xs">
            {t(
              'subscription.cancelAnytime',
              'Cancel anytime. Plans renew monthly; unused top-up words carry over.',
            )}
          </p>
          <button
            type="button"
            onClick={() => navigate('/credits')}
            className="block mx-auto mt-3 text-sm text-primary-500 underline"
          >
            {t('subscription.topUpInstead', 'Just need a few more words? Top up instead')}
          </button>
        </div>
      </div>

      {pendingChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-center text-light-text dark:text-dark-text mb-2">
              {idx(pendingChange.id) > idx(currentPlan)
                ? t('subscription.confirmUpgradeTitle', 'Upgrade to {{plan}}?', {
                    plan: pendingChange.name,
                  })
                : t('subscription.confirmDowngradeTitle', 'Downgrade to {{plan}}?', {
                    plan: pendingChange.name,
                  })}
            </h3>
            <p className="text-sm text-center text-light-text-secondary dark:text-dark-text-secondary mb-6">
              {idx(pendingChange.id) > idx(currentPlan)
                ? t(
                    'subscription.confirmUpgradeMessage',
                    'Your card is charged now for the difference over the rest of this period (nothing if you already paid for this tier this period), and the extra AI words are added right away.',
                  )
                : t(
                    'subscription.confirmDowngradeMessage',
                    'You keep this period’s AI words. The lower price applies from your next invoice; there is no refund for the rest of this period.',
                  )}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPendingChange(null)}
                className="flex-1 py-3 bg-light-surface dark:bg-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => choose(pendingChange, true)}
                disabled={busy}
                className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                {t('subscription.confirmChange', 'Yes, change plan')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-center text-light-text dark:text-dark-text mb-2">
              {t('subscription.cancelConfirmTitle', 'Cancel Subscription?')}
            </h3>
            <p className="text-sm text-center text-light-text-secondary dark:text-dark-text-secondary mb-6">
              {t(
                'subscription.cancelConfirmMessage',
                'Your plan stays active until the end of the current billing period. After that you move to Free and keep any top-up words.',
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setIsLoading(null);
                }}
                className="flex-1 py-3 bg-light-surface dark:bg-dark-border text-light-text dark:text-dark-text font-semibold rounded-xl transition-colors text-sm"
              >
                {t('common.keepIt', 'Keep It')}
              </button>
              <button
                onClick={cancel}
                disabled={busy}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                {isLoading === 'cancel'
                  ? t('subscription.processing', 'Processing...')
                  : t('subscription.confirmCancel', 'Yes, Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
};

export default Subscription;
